import type { DatePickerProps } from '@mantine/dates';
import { DatePicker } from '@mantine/dates';
import { addYears, subYears } from 'date-fns';
import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import { dateToString } from '../../../helpers/dateToString';
import { parsePlainDate } from '../../../helpers/parsePlainDate';

export type DatePickerFiledProps<T extends FieldValues> =
  UseControllerProps<T> &
    Omit<DatePickerProps, 'value' | 'defaultValue' | 'onChange'> & {
      /**
       * Minimum year delta. E.g., if set to 10, the minimum year will be the current year - 10.
       */
      minYearDelta?: number;

      /**
       * Maximum year delta. E.g., if set to 10, the maximum year will be the current year + 10.
       */
      maxYearDelta?: number;

      onChange?: (value: string | null) => void;
    };

/**
 * Calendar-day input wired to react-hook-form. Always emits values as
 * `yyyy-MM-dd` strings (PlainDate) — no time-of-day, no timezone. If you
 * need to capture a date *and* a time-of-day, build a separate component
 * rather than retrofitting a mode flag here.
 */
export function DatePickerField<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  onChange,
  minYearDelta,
  maxYearDelta,
  ...props
}: DatePickerFiledProps<T>) {
  const {
    field: { value, onChange: fieldOnChange, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  const minDate =
    minYearDelta !== undefined && minYearDelta !== null
      ? subYears(new Date(), minYearDelta)
      : undefined;

  const maxDate =
    maxYearDelta !== undefined && maxYearDelta !== null
      ? addYears(new Date(), maxYearDelta)
      : undefined;

  const dateValue = parsePlainDate(value);

  return (
    <DatePicker
      withinPortal
      error={fieldState.error?.message}
      value={dateValue}
      onChange={e => {
        const newDate = dateToString(e);
        fieldOnChange(newDate);
        onChange?.(newDate);
      }}
      minDate={minDate}
      maxDate={maxDate}
      {...field}
      {...props}
    />
  );
}
